import React from 'react';
import { Box, keyframes } from '@chakra-ui/react';

// One-shot celebration: pieces launch from the bottom edge of the viewport and rise across the whole
// page (rather than fanning out of the checkmark, which read as a tiny sparkle).
const rise = keyframes`
  0%   { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translate3d(var(--tx), var(--ty), 0) rotate(var(--r)); opacity: 0; }
`;

/** Subtle breathing glow, for the halo behind a success checkmark. */
export const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.35); }
  50%      { box-shadow: 0 0 0 12px rgba(34,197,94,0); }
`;

const CONFETTI_COLORS = ['#16a34a', '#22c55e', '#a855f7', '#f59e0b', '#eab308'];
const CONFETTI_COUNT = 64;
// Built once at module load: the pieces are decorative, so re-rolling them per render would only
// cost work. Replay is a remount, driven by a `key` on the caller's side.
const CONFETTI = Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
  // Spread evenly across the bottom edge with a little jitter so the launch line isn't a grid.
  left: `${(i / (CONFETTI_COUNT - 1)) * 100 + (Math.random() - 0.5) * 4}%`,
  tx: `${Math.round((Math.random() - 0.5) * 200)}px`,
  ty: `-${Math.round(70 + Math.random() * 50)}vh`,
  r: `${Math.round((Math.random() - 0.5) * 900)}deg`,
  delay: `${(Math.random() * 0.45).toFixed(2)}s`,
  duration: `${(1.7 + Math.random() * 1.1).toFixed(2)}s`,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  size: 7 + Math.round(Math.random() * 6),
  round: i % 3 === 0,
}));

/**
 * Page-wide confetti burst. Fires on mount and runs once; bump a `key` on it to replay.
 *
 * Shared by the in-store scheduling success screen and the product-selection wizard so the two
 * celebrations are the same moment rather than two lookalikes that drift apart.
 */
const Confetti: React.FC = () => (
  <Box
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    overflow="hidden"
    pointerEvents="none"
    zIndex={20}
  >
    {CONFETTI.map((c, i) => (
      <Box
        key={i}
        position="absolute"
        bottom="-24px"
        left={c.left}
        w={`${c.size}px`}
        h={`${c.round ? c.size : c.size * 0.45}px`}
        bg={c.color}
        borderRadius={c.round ? 'full' : '2px'}
        style={{ ['--tx' as any]: c.tx, ['--ty' as any]: c.ty, ['--r' as any]: c.r }}
        animation={`${rise} ${c.duration} cubic-bezier(0.15,0.6,0.35,1) ${c.delay} forwards`}
      />
    ))}
  </Box>
);

export default Confetti;
